import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const AddToOrderDialog = ({
  open,
  onClose,
  readyOrders = [],
  selectedOrderId,
  onSelectOrder,
  onAdd,
  onAddAndSave,
  loading = false,
  t,
}) => {
  const [submittingAction, setSubmittingAction] = useState(null);

  const handleAdd = async () => {
    if (!selectedOrderId || loading) return;
    setSubmittingAction("add");
    try {
      await onAdd(selectedOrderId);
    } catch (_) {
    } finally {
      setSubmittingAction(null);
    }
  };

  const handleAddAndSave = async () => {
    if (!selectedOrderId || loading) return;
    setSubmittingAction("addAndSave");
    try {
      await onAddAndSave(selectedOrderId);
    } catch (_) {
    } finally {
      setSubmittingAction(null);
    }
  };

  const getOrderLabel = (order) => {
    if (!order) return "";
    const storageLocation =
      (order.storage_location && String(order.storage_location).trim()) ||
      "";
    const pucode = order.pucode || order.pickup_code || "";
    if (storageLocation && pucode) {
      return `${storageLocation} (${pucode})`;
    }
    if (storageLocation) return storageLocation;
    return pucode || "";
  };

  const isSubmitting = submittingAction !== null || loading;

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 18px 38px rgba(15, 23, 42, 0.24)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        id="add-to-order-dialog-title"
        sx={{
          m: 0,
          px: 3,
          py: 2.5,
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          fontWeight: 700,
          fontSize: "1.15rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {t("Console.AddToOrder.Title", { defaultValue: "Add to existing order" })}
        <IconButton
          aria-label={t("Global.Close") || "Close"}
          onClick={onClose}
          sx={{
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.18)",
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#64748b", mb: 2 }}>
          {t("Console.AddToOrder.SelectBag", { defaultValue: "Select the bag/order to add the prescription to:" })}
        </Typography>
        <RadioGroup
          value={selectedOrderId != null ? String(selectedOrderId) : ""}
          onChange={(e) => onSelectOrder(e.target.value)}
          sx={{ gap: 0.5 }}
        >
          {readyOrders.map((order) => {
            if (!order || !order.id) return null;
            const label = getOrderLabel(order);
            return (
              <FormControlLabel
                key={order.id}
                value={String(order.id)}
                control={<Radio size="small" />}
                label={label}
                sx={{
                  m: 0,
                  py: 0.75,
                  px: 1.5,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  "&:hover": { backgroundColor: "#f8fafc" },
                }}
              />
            );
          })}
        </RadioGroup>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
        }}
      >
        <Button
          onClick={handleAdd}
          disabled={!selectedOrderId || isSubmitting}
          variant="contained"
          startIcon={
            submittingAction === "add" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{
            minWidth: "120px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
            },
          }}
        >
          {t("Console.AddToOrder.Add", { defaultValue: "Add" })}
        </Button>
        <Button
          onClick={handleAddAndSave}
          disabled={!selectedOrderId || isSubmitting}
          variant="outlined"
          startIcon={
            submittingAction === "addAndSave" ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AddCircleOutlineIcon />
            )
          }
          sx={{
            minWidth: "140px",
            textTransform: "none",
            borderRadius: 2,
            fontWeight: 600,
            borderColor: "#7c3aed",
            color: "#7c3aed",
            "&:hover": {
              borderColor: "#6d28d9",
              backgroundColor: "rgba(124, 58, 237, 0.08)",
            },
          }}
        >
          {t("Console.AddToOrder.AddAndSave", { defaultValue: "Add & Print" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddToOrderDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  readyOrders: PropTypes.arrayOf(PropTypes.object),
  selectedOrderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelectOrder: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onAddAndSave: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default AddToOrderDialog;



// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/AddToOrderDialog.jsx